import { useEffect, useState } from "react";
import AccountLayout from "../components/AccountLayout.jsx";
import ClientLayout from "../components/ClientLayout.jsx";
import { account } from "../lib/appwrite.js";
import { ProfileFieldsSkeleton } from "../components/loading/SkeletonBlocks.jsx";
import { useCurrentUser } from "../hooks/useCurrentUser.js";
import { useRole } from "../hooks/useRole.js";

export default function SettingsPage() {
  const role = useRole();
  const { user, isLoading, refetch } = useCurrentUser();
  const [name, setName] = useState("");
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState(null);

  const [resetState, setResetState] = useState("idle");
  const [resetError, setResetError] = useState(null);

  useEffect(() => {
    if (user) setName(user.name ?? "");
  }, [user]);

  async function handleSaveProfile() {
    setSaveState("saving");
    setSaveError(null);
    try {
      await account.updateName(name.trim());
      refetch();
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (err) {
      setSaveError(err?.message ?? "Could not save your changes.");
      setSaveState("idle");
    }
  }

  const nameUnchanged = (user?.name ?? "") === name.trim();

  async function handleSendPasswordReset() {
    if (!user?.email) return;

    setResetState("sending");
    setResetError(null);
    try {
      const resetUrl = `${window.location.origin}/reset-password`;
      await account.createRecovery(user.email, resetUrl);
      setResetState("sent");
    } catch (err) {
      setResetError(err?.message ?? "Could not send the reset link. Please try again.");
      setResetState("idle");
    }
  }

  return (
    <LayoutWrapper role={role}>
      <section className="rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
        <h2 className="mb-4 text-sm font-semibold tracking-[-0.15px] text-hc-text">
          Profile
        </h2>
        {isLoading ? (
          <ProfileFieldsSkeleton />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-hc-text-dim">Display name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-[10px] border border-hc-surface-border bg-hc-page-shell px-3 py-2 text-sm text-hc-text placeholder:text-hc-text-dim focus:border-hc-primary/50 focus:outline-none focus:ring-1 focus:ring-hc-primary/30"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-hc-text-dim">Email address</label>
              <input
                type="email"
                value={user?.email ?? ""}
                readOnly
                className="cursor-not-allowed rounded-[10px] border border-hc-surface-border bg-hc-page-shell px-3 py-2 text-sm text-hc-text-muted focus:outline-none"
              />
            </div>
          </div>
        )}
        {saveError && (
          <p className="mt-3 text-xs text-hc-error">{saveError}</p>
        )}
        <button
          type="button"
          onClick={handleSaveProfile}
          disabled={isLoading || saveState === "saving" || nameUnchanged}
          className="mt-5 rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {saveState === "saving"
            ? "Saving…"
            : saveState === "saved"
              ? "Saved"
              : "Save changes"}
        </button>
      </section>

      <section className="rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
        <h2 className="mb-1 text-sm font-semibold tracking-[-0.15px] text-hc-text">
          Change password
        </h2>
        <p className="mb-4 text-xs text-hc-text-dim">
          {resetState === "sent" ? (
            <>
              We sent a password reset link to{" "}
              <span className="font-medium text-hc-text">{user?.email}</span>. Open the link in
              your email to choose a new password. The link expires in 1 hour.
            </>
          ) : (
            <>
              We&apos;ll email you a link to set a new password.
            </>
          )}
        </p>
        {resetError && (
          <p className="mb-4 text-xs text-hc-error">{resetError}</p>
        )}
        {resetState === "sent" ? (
          <button
            type="button"
            onClick={() => setResetState("idle")}
            className="rounded-[10px] border border-hc-surface-border px-4 py-2 text-sm font-semibold text-hc-text hover:bg-white/5"
          >
            Send another link
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSendPasswordReset}
            disabled={isLoading || !user?.email || resetState === "sending"}
            className="rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {resetState === "sending" ? "Sending…" : "Email me a reset link"}
          </button>
        )}
      </section>
    </LayoutWrapper>
  );
}

function LayoutWrapper({ role, children }) {
  if (role === "client") {
    return <ClientLayout>{children}</ClientLayout>;
  }
  return <AccountLayout>{children}</AccountLayout>;
}
